import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import useFlatFormikValues from '../../../useFlatFormikValues'

import RadioBlock from '../../../../../../components/Form/RadioBlock'
import Button from '../../../../../../components/Button'
import AdPreviewSoundControlWrapper from '../../../../../../features/components/FacebookPreviews/reusablePreviewComponents/AdPreviewSoundControlWrapper'
import Field from '../../../../../../components/Form/Field'
import FacebookSingleMediaAdPreview from '../../../../../../features/components/FacebookPreviews/FacebookSingleMediaAdPreview'
import FacebookExistingPostPreview from '../../../../../../features/components/FacebookPreviews/FacebookExistingPostPreview'

import { formatOptionsList } from '../../../../../../features/formatters'
import { getCTAListLimitedToLeadGenerationObjective } from '../../../helpers'

import { choicesCTAByGoalSelector } from '../../../../../../modules/selectors/choices'

import {
  AD_DESCRIPTION,
  CALL_TO_ACTION,
  CALL_TO_ACTION_LEARN_MORE,
  CALL_TO_ACTION_OPTION,
  CALL_TO_ACTION_SELECT,
  FORMAT_EXISTING_POST,
  FORMAT_OPTION,
  FORMAT_SINGLE,
  LEARN_MORE
} from '../../../fields'

import { AdFacebookCreateContext } from '../../../../formsContexts/AdFacebookCreateFormContextProvider'

import { LEAD_GENERATION } from '../../../../../../constants/campaigns'

import useStyles from '../../../../../../styles/common/stepForms'

const fields = [AD_DESCRIPTION]

const CallToActionStep = ({ formik, handleStepChange, isActive }) => {
  const { t } = useTranslation()
  const classes = useStyles()

  const { values, setFieldValue } = formik

  const { campaignObjective, lineItem } = useContext(AdFacebookCreateContext)
  const actionsList = useSelector(choicesCTAByGoalSelector(campaignObjective))

  const flatFormikValues = useFlatFormikValues({
    formikValues: values,
    allowUseSingleMediaItem: false
  })

  const formattedActionsList = useMemo(() => {
    const list =
      lineItem.objective === LEAD_GENERATION ? getCTAListLimitedToLeadGenerationObjective(actionsList) : actionsList

    return formatOptionsList({
      list: list,
      labelName: 'label',
      valueName: 'name'
    })
  }, [actionsList, lineItem.objective])

  const handleContinue = useCallback(() => {
    handleStepChange(fields)
  }, [handleStepChange])

  const [stepWasOpened, setStepWasOpened] = useState(false)
  useEffect(() => {
    // trigger initial set for CALL_TO_ACTION which represents on ActionButton
    // this should be triggered only during first time step opening
    if (!stepWasOpened && isActive) {
      setFieldValue(CALL_TO_ACTION, LEARN_MORE)
      setStepWasOpened(true)
    }
  }, [stepWasOpened, isActive, setFieldValue])

  const learnMoreActionHandler = () => {
    setFieldValue(CALL_TO_ACTION_OPTION, CALL_TO_ACTION_LEARN_MORE)
    setFieldValue(CALL_TO_ACTION, LEARN_MORE)
  }

  const renderAdPreview = useCallback(() => {
    if (values[FORMAT_OPTION] === FORMAT_SINGLE) {
      return (
        <AdPreviewSoundControlWrapper formikValues={flatFormikValues} isSoundDisabled={!isActive}>
          <FacebookSingleMediaAdPreview elementInProgress={CALL_TO_ACTION_OPTION} formikValues={flatFormikValues} />
        </AdPreviewSoundControlWrapper>
      )
    }

    if (values[FORMAT_OPTION] === FORMAT_EXISTING_POST) {
      return <FacebookExistingPostPreview values={values} isInProgress={true} />
    }
  }, [flatFormikValues, values, isActive])

  return (
    <>
      <div className={classes.stepTitle}>{t('Call to action')}</div>
      <div className={classes.stepBody}>
        {renderAdPreview()}
        <RadioBlock
          setFieldValue={setFieldValue}
          id="radio_call_to_action_option_learn_more"
          name={CALL_TO_ACTION_OPTION}
          value={CALL_TO_ACTION_LEARN_MORE}
          selectedValue={values[CALL_TO_ACTION_OPTION]}
          label={t('Learn more')}
          onChange={learnMoreActionHandler}
        />
        <RadioBlock
          setFieldValue={setFieldValue}
          id="radio_call_to_action_choices"
          name={CALL_TO_ACTION_OPTION}
          value={CALL_TO_ACTION_SELECT}
          selectedValue={values[CALL_TO_ACTION_OPTION]}
          label={t('Select Call To Action')}
        >
          <Field
            placeholder={t('CALL-TO-ACTION')}
            formik={formik}
            name={CALL_TO_ACTION}
            options={formattedActionsList}
          />
        </RadioBlock>
      </div>
      <div className={classes.stepFooter}>
        <Button type="button" className="dark" onClick={handleContinue}>
          {t('Continue')}
        </Button>
      </div>
    </>
  )
}

export default CallToActionStep
